import { useEffect, useState } from "react";
import Select from "react-select";
import { Checkbox, Grid, Popup } from "semantic-ui-react";
import { useGetIngredientsSupplierList, useGetIngredientWholeList } from "../../../api/ingredients";
import FilterIcon from "../../../assets/commonIcon/FilterIcon";
import { CustomButton } from "../../../components/button/Button";
import { TitleView } from "../../../components/titleView/TitleView";
import {
  IngredientDataTypeLabel,
  IngredientFilterKeys
} from "../../../config/enums/IngredientType.enum";
import { saveIngredientFilters } from "../../../utils/cacheStorage";

const IngredientFilter = ({ refetchWithFilters }: any) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState<any>([] as any);
  const [selectedSuppliers, setSelectedSuppliers] = useState<any>([] as any);
  const [selectedDataTypes, setSelectedDataTypes] = useState<any>([] as any);
  const [sortBy, setSortBy] = useState<number>(3);

  useEffect(() => {
    const getStoredFilters = () => {
      const getParsedItem = (key: string) => {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : [];
      };

      const ingredientIds = getParsedItem(IngredientFilterKeys.INGREDIENT_IDS);
      const supplierIds = getParsedItem(IngredientFilterKeys.SUPPLIER_IDS);
      const dataTypeIds = getParsedItem(IngredientFilterKeys.DATA_TYPE_IDS);
      const status = localStorage.getItem(IngredientFilterKeys.STATUS);
      return {
        ingredientIds,
        supplierIds,
        dataTypeIds,
        status: status ? Number(status) : 3
      };
    };

    const { ingredientIds, supplierIds, dataTypeIds, status } = getStoredFilters();

    if (ingredientIds.length || supplierIds.length || dataTypeIds.length || status !== undefined) {
      setSelectedIngredients(ingredientIds);
      setSelectedSuppliers(supplierIds);
      setSelectedDataTypes(dataTypeIds);
      setSortBy(status);
    }
  }, []);

  const { data: ingredientListByProduct } = useGetIngredientWholeList(false);
  const { data: ingredientSupplierList } = useGetIngredientsSupplierList(false);

  const currentIngredientData = ingredientListByProduct?.map((item: any) => {
    return {
      value: item.id,
      label: item.title
    };
  });

  const currentSupplierData = ingredientSupplierList?.map((item: any) => {
    return {
      value: item.id,
      label: item.title
    };
  });

  const ingredientDataTypeOptions = Object.entries(IngredientDataTypeLabel).map(([key, label]) => ({
    value: key,
    label
  }));

  const resetValue = () => {
    setSelectedIngredients([]);
    setSelectedSuppliers([]);
    setSelectedDataTypes([]);
    setSortBy(3);
    const emptyIngredients: any[] = [];
    const emptySuppliers: any[] = [];
    const emptyDataTypes: any[] = [];
    const defaultSort = 3;

    setSelectedIngredients(emptyIngredients);
    setSelectedSuppliers(emptySuppliers);
    setSelectedDataTypes(emptyDataTypes);
    setSortBy(defaultSort);

    saveIngredientFilters(emptyIngredients, emptySuppliers, emptyDataTypes, defaultSort);
    refetchWithFilters(emptyIngredients, emptySuppliers, emptyDataTypes, defaultSort);
    setIsPopupOpen(false);
  };

  const filterValue = () => {
    saveIngredientFilters(selectedIngredients, selectedSuppliers, selectedDataTypes, sortBy);

    const currentSelectedIngredients = selectedIngredients.map((e: any) => e.value);
    const currentSelectedSuppliers = selectedSuppliers.map((e: any) => e.value);
    const currentSelectedDataTypes = selectedDataTypes.map((e: any) => e.value);
    refetchWithFilters(
      currentSelectedIngredients,
      currentSelectedSuppliers,
      currentSelectedDataTypes,
      sortBy
    );
    setIsPopupOpen(false);
  };

  return (
    <Popup
      open={isPopupOpen}
      on="click"
      onOpen={() => setIsPopupOpen(true)}
      onClose={() => {
        setIsPopupOpen(false);
      }}
      size="large"
      position={"bottom left"}
      trigger={
        <Grid>
          <CustomButton
            className="filterButton"
            iconWithText
            iconChild={<FilterIcon />}
            buttonOutLine
            theme="green"
            title="Filters"
            iconChildTextCustom={"productCustomBase"}
          />
        </Grid>
      }
    >
      <Grid className="productFilterMainView">
        <Grid.Column>
          <TitleView title={"Sort By"} />
          <Grid>
            <Grid.Column computer={8} tablet={8} mobile={16}>
              <p className={`productFilterTitle`}>Ingredient Name</p>
              <Select
                value={selectedIngredients}
                onChange={(e: any) => {
                  setSelectedIngredients(e);
                }}
                isMulti
                options={currentIngredientData || []}
              />
            </Grid.Column>
            <Grid.Column computer={8} tablet={8} mobile={16}>
              <p className={`productFilterTitle`}>Supplier Name</p>
              <Select
                value={selectedSuppliers}
                onChange={(e: any) => {
                  setSelectedSuppliers(e);
                }}
                isMulti
                options={currentSupplierData || []}
              />
            </Grid.Column>
            <Grid.Column computer={8} tablet={8} mobile={16}>
              <p className={`productFilterTitle`}>Data Type</p>
              <Select
                value={selectedDataTypes}
                onChange={(e: any) => {
                  setSelectedDataTypes(e);
                }}
                isMulti
                options={ingredientDataTypeOptions || []}
              />
            </Grid.Column>
          </Grid>
          <p className="productFilterSortResultTitle mt-1">Sort Result</p>
          <Grid className="pt-0">
            <Grid.Column computer={5} tablet={8} mobile={16} className="pt-0">
              <Checkbox
                onClick={() => setSortBy(1)}
                checked={sortBy === 1}
                radio
                label="With calculation"
              />
            </Grid.Column>
            <Grid.Column computer={5} tablet={8} mobile={16} className="pt-0">
              <Checkbox
                onClick={() => setSortBy(2)}
                checked={sortBy === 2}
                radio
                label="With out calculation"
              />
            </Grid.Column>
            <Grid.Column computer={5} tablet={8} mobile={16} className="pt-0">
              <Checkbox
                onClick={() => setSortBy(3)}
                checked={sortBy === 3}
                radio
                label="Not relevant"
              />
            </Grid.Column>
          </Grid>
          <Grid className="pt-0">
            <Grid.Column computer={16} tablet={16} mobile={16} className="productFilterButton">
              <CustomButton
                buttonOutLine
                customBase="productFilterCustomButton"
                theme="green"
                title="Reset"
                onClick={resetValue}
              />
              <CustomButton theme="green" title="Apply" onClick={filterValue} />
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>
    </Popup>
  );
};

export default IngredientFilter;
